import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Switch, Button, message, Space } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { API_ENDPOINTS } from '../utils/apiConfig';

const { Option } = Select;

interface User {
  username?: string;
  role?: 'Admin' | 'Leader' | 'Employee';
  status?: 'Active' | 'Inactive';
  permissions?: string[];
  isActive?: boolean;
  [key: string]: any;
}

interface EditUserModalProps {
  visible: boolean;
  user: User | null;
  onCancel: () => void;
  onSuccess: () => void;
  userRole: string;
}

interface Category { _id: string; name: string; isActive: boolean; }

const EditUserModal: React.FC<EditUserModalProps> = ({
  visible,
  user,
  onCancel,
  onSuccess,
  userRole,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    if (visible) {
      // Fetch categories from backend
      const fetchCategories = async () => {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(API_ENDPOINTS.CATEGORIES_ALL, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const data = await response.json();
          setCategories(Array.isArray(data) ? data : []);
        } catch (error) {
          setCategories([]);
        }
      };
      fetchCategories();
    }
  }, [visible]);

  useEffect(() => {
    if (visible && user) {
      form.setFieldsValue({
        roleTitle: user.roleTitle || '',
        status: user.status || 'Active',
        permissions: user.permissions || [],
        isActive: user.isActive !== undefined ? user.isActive : true,
      });
    }
  }, [visible, user, form]);

  const handleSubmit = async (values: any) => {
    if (!user) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.EDIT_USER}/${user.username}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update user');
      }

      const result = await response.json();
      message.success(result.message || 'User updated successfully');
      onSuccess();
      onCancel();
    } catch (error: any) {
      message.error(error.message || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  // Check if current user can edit this user
  const canEditUser = () => {
    if (!user || !userRole) return false;
    
    // Admin can edit anyone
    if (userRole === 'Admin') return true;
    
    // Leader can edit employees and other leaders, but not admins
    if (userRole === 'Leader') {
      return user.role !== 'Admin';
    }
    
    // Employee cannot edit anyone
    return false;
  };

  if (!canEditUser()) {
    return (
      <Modal
        title="Edit User"
        open={visible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Close
          </Button>
        ]}
      >
        <p>You don't have permission to edit this user.</p>
      </Modal>
    );
  }

  return (
    <Modal
      title={
        <Space>
          <EditOutlined />
          Edit User: {user?.name || user?.username || 'Unknown User'}
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          status: 'Active',
          isActive: true,
          permissions: [],
        }}
      >
        {/*<Form.Item*/}
        {/*  label="Status"*/}
        {/*  name="status"*/}
        {/*  rules={[{ required: true, message: 'Please select status' }]}*/}
        {/*>*/}
        {/*  <Select>*/}
        {/*    <Option value="Active">Active</Option>*/}
        {/*    <Option value="Inactive">Inactive</Option>*/}
        {/*  </Select>*/}
        {/*</Form.Item>*/}

        <Form.Item
          label="Block Categories"
          name="permissions"
          rules={[{ required: false }]}
        >
          <Select
            mode="multiple"
            placeholder="Select categories"
            allowClear
          >
            {categories.map(cat => (
              <Option key={cat._id} value={cat.name}>
                {cat.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {/*<Form.Item*/}
        {/*  label="Account Active"*/}
        {/*  name="isActive"*/}
        {/*  valuePropName="checked"*/}
        {/*>*/}
        {/*  <Switch />*/}
        {/*</Form.Item>*/}

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<SaveOutlined />}
            >
              Save Changes
            </Button>
            <Button
              onClick={handleCancel}
              icon={<CloseOutlined />}
            >
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditUserModal; 